import { faroCoreApiClient } from "@api/faro-core-api-client";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useToast } from "@flat-ui";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { AllMemberTypes } from "@custom-types/base-types";
import { useAppParams } from "@router/router-helper";
import { isAdmMember } from "@utils/type-guards";
import { useCallback } from "react";
import { LocationProp } from "@utils/track-event/event-types";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

interface Props extends LocationProp {
  /** The member to resend invitation to workspace */
  member: AllMemberTypes;
}

interface ReturnProps {
  /** Whether to hide resend invitation */
  shouldHideResendInvitation: boolean;

  /** Function to resend invitation */
  onResendInvitationClick: () => Promise<void>;
}

/**
 * Resend invite for a single unregistered member to workspace
 * This component is hidden by default and it is shown on table hover using ${SHOW_ON_HOVER_CLASS}.
 */
export function useResendInvitationToWorkspace({
  member,
  location,
}: Props): ReturnProps {
  const { companyId: companyIdFromRoute } = useAppParams();
  const { handleErrorWithToast } = useErrorHandler();
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();

  // Use company ID from member prop when available (e.g. search for members), otherwise fall back to route param
  // This makes sure that resend invitation is shown only if the member belongs to one company in search results and it works
  // both in workspace members page and in search results which don't have companyId in the route
  const memberCompanyId =
    isAdmMember(member) && member.companies.length === 1
      ? member.companies[0].id
      : undefined;
  const companyId = memberCompanyId ?? companyIdFromRoute;

  const onResendInvitationClick = useCallback(async (): Promise<void> => {
    trackEvent({
      name: WorkspaceEvents.resendInvitationToWorkspace,
      props: {
        location,
      },
    });

    if (!companyId) {
      return;
    }

    try {
      // No need to be an asyncThunk as we don't need to update the store
      await faroCoreApiClient.admin.resendCompanyMembershipInvite({
        companyId,
        userIdentity: member.email,
      });
      openToast({
        title: "Invitation sent successfully!",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `resendInvite-${Date.now().toString()}`,
        title: "Could not send invitation",
        error,
      });
    }
  }, [
    companyId,
    handleErrorWithToast,
    location,
    member.email,
    openToast,
    trackEvent,
  ]);

  return {
    shouldHideResendInvitation:
      FaroCoreApiUtils.Member.isMemberActive(member) ||
      FaroCoreApiUtils.Member.isTeam(member) ||
      !companyId,
    onResendInvitationClick,
  };
}
